/** Dagen van de week (zondag = 0 in veel systemen) */
export type Weekday =
  | 'zondag'
  | 'maandag'
  | 'dinsdag'
  | 'woensdag'
  | 'donderdag'
  | 'vrijdag'
  | 'zaterdag'

/** Alle weekdagen (volgorde o.a. voor normalisatie / defaults) */
export const WEEKDAYS: Weekday[] = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag']

/** Weergave volgens maandag → zondag (gebruikelijk in NL/BE) */
export const WEEKDAYS_MON_FIRST: Weekday[] = [
  'maandag',
  'dinsdag',
  'woensdag',
  'donderdag',
  'vrijdag',
  'zaterdag',
  'zondag',
]

/** Openingstijden voor één dag; start/end in 24u-formaat HH:mm */
export interface OpeningHoursDay {
  open: boolean
  start?: string
  end?: string
}

/** Bedrijfsuren per weekdag */
export type BusinessHours = Partial<Record<Weekday, OpeningHoursDay>>

/** Bedrijfsgegevens van de salon (Instellingen) */
export interface SalonSettings {
  /** Naam van de zaak (weergave) */
  businessName?: string
  /** Uniek URL-pad voor de boekingspagina: /salon/:slug (los van de bedrijfsnaam) */
  slug?: string | null
  /** E-mail van de eigenaar (vaak gekoppeld aan login, niet bewerkbaar in formulier) */
  businessOwnerEmail?: string
  /** Voornaam eigenaar (uit registratie, bewerkbaar in Instellingen) */
  businessOwnerVoornaam?: string
  /** Achternaam eigenaar (uit registratie, bewerkbaar in Instellingen) */
  businessOwnerAchternaam?: string
  /** Telefoonnummer van de eigenaar */
  businessOwnerPhone?: string
  /** Adres van de zaak (enkel veld; wordt ook samengesteld uit addressStreet + postcode + city) */
  address?: string
  /** Straat en huisnummer (zoals in onboarding) */
  addressStreet?: string
  /** Postcode */
  postcode?: string
  /** Gemeente */
  city?: string
  /** Instagram-handle, bijv. @yourbusiness */
  instagram?: string
  /** Facebook-URL, bijv. facebook.com/yourbusiness */
  facebook?: string
  /** Website-URL */
  website?: string
  /** TikTok-handle of -URL, bijv. @jouwzaak of tiktok.com/@jouwzaak */
  tiktok?: string
  /** Openingstijden per weekdag */
  businessHours?: BusinessHours
  /** Bannerkleur (hex) voor boekingspagina; leeg = fallback in frontend */
  bannerColor?: string
  /** Bannerfoto-URL na upload in Storage; alleen via upload, geen handmatige URL */
  bannerImageUrl?: string
  /** Publiek zichtbaar in de salon-markt/zoekpagina (bijv. guapaa.com/salon). Standaard false. */
  listOnMarketplace?: boolean
  /** Online boekingen: wanneer mogen klanten boeken (immediate = vandaag) */
  bookingLeadTime?: BookingLeadTime
  /** Online boekingen: hoe ver vooruit mogen klanten boeken */
  bookingAdvanceMax?: BookingAdvanceMax
  /** Online boekingen: tot wanneer mogen klanten online annuleren/verzetten; na deze periode moeten ze bellen */
  cancelRescheduleCutoff?: CancelRescheduleCutoff
  /** Vrij tekstveld: annuleringsbeleid op de boekingspagina */
  annuleringsbeleid?: string
}

/** Min. minuten vóór begintijd dat klant moet boeken. "0" = onmiddellijk; "1440" = 1 dag, etc. Legacy: immediate, 1_day, 2_days, 3_days, 1_week worden in code omgezet. */
export type BookingLeadTime = string
export type BookingAdvanceMax = '1_month' | '3_months' | '6_months' | '12_months'
/** Min. minuten vóór afspraak dat online annuleren nog mag. "any" = altijd; "30", "60", "1440" (24u), etc. Legacy: 24h, 48h, 72h, 1_week worden in code omgezet. */
export type CancelRescheduleCutoff = string

/** Online voorschot bij boeken (Mollie) */
export type BookingDepositMode = 'none' | 'fixed' | 'percentage'

export interface BookingDepositSettings {
  mode: BookingDepositMode
  /** Vast voorschot in EUR (effectief ten hoogste het orderbedrag) */
  fixedAmount: number
  /** Percentage 0–100 van het orderbedrag */
  percentage: number
}

/** Subscription-status uit de database-cache op salons.subscription_status (+ incomplete via subscriptions). */
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'incomplete' | 'canceled' | 'blocked'

/** Abonnement/trial en modules per salon (Mijn abonnement-pagina) */
export interface SalonAbonnement {
  trialEndsAt: string | null
  abonnementBevestigdAt: string | null
  modulePlanning: boolean
  moduleKassa: boolean
  /** Betaalde abonnementsmodule Betaling (sidebar / facturatie). */
  moduleBetaling: boolean
  /** Klanten rekenen af bij online boeken. Los van moduleBetaling. */
  onlineBetalenBijBoeken: boolean
  /** Effectieve status voor UI (inclusief incomplete uit subscriptions). */
  subscriptionStatus: SubscriptionStatus
  /** Cache op salons.subscription_status — voor toegang tijdens incomplete checkout. */
  accessStatus: SubscriptionStatus
  /** Einde grace-window bij past_due (uit subscriptions). */
  graceUntil: string | null
  /** Einde huidige betaalperiode bij active (uit subscriptions). */
  currentPeriodEnd: string | null
}
